/**
 * Снятие геометки перед действием.
 *
 * Вызывается строго внутри обработчика нажатия — иначе Telegram и браузер
 * просто откажут. Возвращает undefined при любом отказе, таймауте или
 * выключенных геометках: действие после этого продолжается как обычно,
 * без ошибок и без объяснений от няни.
 */

import { useCallback, useRef } from 'react'

import { ACCURACY_TRUSTED_M, formatAccuracy } from '@/features/geo/accuracy'
import type { GeoInput } from '@/shared/api/day'
import { requestLocation } from '@/shared/telegram'
import { toast } from '@/shared/ui/toast'

export function useGeoCapture(active: boolean) {
  // Подсказку про неточную точку показываем один раз за заход: повторять её
  // на каждое нажатие значило бы отчитывать няню, а не помогать ей.
  const hinted = useRef(false)

  return useCallback(async (): Promise<GeoInput | undefined> => {
    if (!active) return undefined

    const result = await requestLocation()
    if (!result.ok || result.lat === undefined || result.lon === undefined) {
      // отказ — нормальный сценарий, а не ошибка: молчим
      return undefined
    }

    // Точность в километры почти всегда значит, что телефон отдаёт Telegram
    // только примерное местоположение. Точку всё равно сохраняем — решает
    // няня, но говорим, как сделать её полезной.
    if (
      result.accuracyM !== undefined &&
      result.accuracyM > ACCURACY_TRUSTED_M &&
      !hinted.current
    ) {
      hinted.current = true
      toast.show(
        `Геометка получилась неточной (${formatAccuracy(result.accuracyM)}). ` +
          'Если хотите, чтобы по ней было видно место, включите для Telegram точную ' +
          'геолокацию в настройках телефона.',
      )
    }

    return {
      lat: result.lat.toFixed(6),
      lon: result.lon.toFixed(6),
      accuracy_m: result.accuracyM,
      captured_at: result.capturedAt,
    }
  }, [active])
}
